import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import {
  Plus, Send, Edit2, Trash2, Save, MoreVertical, CreditCard, AlertTriangle,
  ExternalLink, RefreshCw, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const Invoices = ({ invoices, contacts, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [sendingReminder, setSendingReminder] = useState(null);
  const [creatingCheckout, setCreatingCheckout] = useState(null);
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0 }]);
  const [formData, setFormData] = useState({
    contact_id: "none", tax_rate: 0, due_date: "", notes: ""
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(value || 0);
  };

  const filteredInvoices = invoices.filter(inv => filterStatus === "all" || inv.status === filterStatus);

  const getContactName = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact ? contact.name : 'Unknown';
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-slate-100 text-slate-600",
      sent: "bg-blue-100 text-blue-600",
      paid: "bg-green-100 text-green-600",
      overdue: "bg-red-100 text-red-600",
      cancelled: "bg-slate-100 text-slate-400"
    };
    return styles[status] || styles.draft;
  };

  const handleOpenDialog = (invoice = null) => {
    if (invoice) {
      setEditingInvoice(invoice);
      setItems(invoice.items || [{ description: "", quantity: 1, unit_price: 0 }]);
      setFormData({
        contact_id: invoice.contact_id || "none",
        tax_rate: invoice.tax_rate || 0,
        due_date: invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : "",
        notes: invoice.notes || ""
      });
    } else {
      setEditingInvoice(null);
      setItems([{ description: "", quantity: 1, unit_price: 0 }]);
      setFormData({ contact_id: "none", tax_rate: 0, due_date: "", notes: "" });
    }
    setDialogOpen(true);
  };

  const addItem = () => {
    setItems([...items, { description: "", quantity: 1, unit_price: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const calculateTotal = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const taxAmount = subtotal * (formData.tax_rate / 100);
    return { subtotal, taxAmount, total: subtotal + taxAmount };
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        contact_id: formData.contact_id === "none" ? null : formData.contact_id,
        items: items.map(item => ({
          description: item.description,
          quantity: parseInt(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0
        })),
        tax_rate: parseFloat(formData.tax_rate) || 0,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        notes: formData.notes || null
      };

      if (editingInvoice) {
        await axios.put(`${API}/invoices/${editingInvoice.id}`, payload);
      } else {
        await axios.post(`${API}/invoices`, payload);
      }
      setDialogOpen(false);
      onRefresh();
    } catch (error) {
      console.error("Error saving invoice:", error);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/send`);
      onRefresh();
    } catch (error) {
      console.error("Error sending invoice:", error);
    }
  };

  const handleSendReminder = async (invoiceId) => {
    setSendingReminder(invoiceId);
    try {
      await axios.post(`${API}/invoices/${invoiceId}/send-reminder`);
      alert("Reminder sent successfully!");
    } catch (error) {
      console.error("Error sending reminder:", error);
      alert("Failed to send reminder");
    } finally {
      setSendingReminder(null);
    }
  };

  const handleCreateCheckout = async (invoiceId) => {
    setCreatingCheckout(invoiceId);
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/checkout`, {}, {
        headers: { 'Origin': window.location.origin }
      });
      if (response.data.checkout_url) {
        window.open(response.data.checkout_url, '_blank');
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      alert("Failed to create payment link");
    } finally {
      setCreatingCheckout(null);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (window.confirm("Are you sure you want to delete this invoice?")) {
      try {
        await axios.delete(`${API}/invoices/${invoiceId}`);
        onRefresh();
      } catch (error) {
        console.error("Error deleting invoice:", error);
      }
    }
  };

  const { subtotal, taxAmount, total } = calculateTotal();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-cabinet text-3xl font-bold text-slate-900">Invoices</h2>
          <p className="text-slate-500 font-manrope">Manage billing and payments</p>
        </div>
        <Button 
          data-testid="add-invoice-btn"
          onClick={() => handleOpenDialog()} 
          className="bg-royal-blue-700 hover:bg-royal-blue-800"
        >
          <Plus size={18} className="mr-2" /> Create Invoice
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48" data-testid="invoice-status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Invoices</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">Invoice #</th>
                  <th className="text-left p-4 font-medium text-slate-600">Customer</th>
                  <th className="text-left p-4 font-medium text-slate-600">Amount</th>
                  <th className="text-left p-4 font-medium text-slate-600">Status</th>
                  <th className="text-left p-4 font-medium text-slate-600">Due Date</th>
                  <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="p-4 font-medium text-slate-900">{invoice.invoice_number}</td>
                    <td className="p-4 text-slate-600">{getContactName(invoice.contact_id)}</td>
                    <td className="p-4 font-semibold text-slate-900">{formatCurrency(invoice.total)}</td>
                    <td className="p-4">
                      <Badge className={getStatusBadge(invoice.status)}>
                        {invoice.status}
                      </Badge>
                    </td>
                    <td className="p-4 text-slate-600">
                      {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {invoice.status === 'draft' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSendInvoice(invoice.id)}
                            data-testid={`send-invoice-${invoice.id}`}
                          >
                            <Send size={14} className="mr-1" /> Send
                          </Button>
                        )}
                        {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-green-600 border-green-200 hover:bg-green-50"
                            onClick={() => handleCreateCheckout(invoice.id)}
                            disabled={creatingCheckout === invoice.id}
                            data-testid={`pay-invoice-${invoice.id}`}
                          >
                            {creatingCheckout === invoice.id ? (
                              <RefreshCw size={14} className="mr-1 animate-spin" />
                            ) : (
                              <CreditCard size={14} className="mr-1" />
                            )}
                            Pay Now
                          </Button>
                        )}
                        {invoice.status === 'overdue' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-orange-600 border-orange-200 hover:bg-orange-50"
                            onClick={() => handleSendReminder(invoice.id)}
                            disabled={sendingReminder === invoice.id}
                            data-testid={`remind-invoice-${invoice.id}`}
                          >
                            {sendingReminder === invoice.id ? (
                              <RefreshCw size={14} className="mr-1 animate-spin" />
                            ) : (
                              <AlertTriangle size={14} className="mr-1" />
                            )}
                            Remind
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenDialog(invoice)}>
                              <Edit2 size={14} className="mr-2" /> Edit
                            </DropdownMenuItem>
                            {invoice.payment_url && (
                              <DropdownMenuItem onClick={() => window.open(invoice.payment_url, '_blank')}>
                                <ExternalLink size={14} className="mr-2" /> Payment Link
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => handleDelete(invoice.id)}
                              className="text-red-600"
                            >
                              <Trash2 size={14} className="mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredInvoices.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No invoices found. Create your first invoice!
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoice Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-cabinet">{editingInvoice ? 'Edit Invoice' : 'Create Invoice'}</DialogTitle>
            <DialogDescription>Add line items and invoice details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select value={formData.contact_id} onValueChange={(v) => setFormData({...formData, contact_id: v})}>
                  <SelectTrigger data-testid="invoice-contact-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select customer...</SelectItem>
                    {contacts.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input 
                  type="date"
                  data-testid="invoice-due-date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Line Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus size={14} className="mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <Input 
                      placeholder="Description"
                      className="flex-1"
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                    />
                    <Input 
                      type="number"
                      placeholder="Qty"
                      className="w-20"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                    />
                    <Input 
                      type="number"
                      placeholder="Price"
                      className="w-28"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                    />
                    <span className="w-24 text-right pt-2 font-medium">
                      {formatCurrency(item.quantity * item.unit_price)}
                    </span>
                    {items.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)}>
                        <X size={16} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input 
                  type="number"
                  min="0"
                  max="100"
                  data-testid="invoice-tax-rate"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData({...formData, tax_rate: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input 
                  placeholder="Optional notes..."
                  data-testid="invoice-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-2 text-right">
              <div className="flex justify-between">
                <span className="text-slate-600">Subtotal:</span>
                <span className="font-medium">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Tax ({formData.tax_rate}%):</span>
                <span className="font-medium">{formatCurrency(taxAmount)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold">
                <span>Total:</span>
                <span className="text-royal-blue-700">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button 
              data-testid="save-invoice-btn"
              onClick={handleSubmit} 
              className="bg-royal-blue-700 hover:bg-royal-blue-800"
              disabled={formData.contact_id === "none" || items.every(i => !i.description)}
            >
              <Save size={16} className="mr-2" /> {editingInvoice ? 'Update' : 'Create'} Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

